import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, MapPin, User, X } from 'lucide-react';

const deduplicateAddress = (addr) => {
  if (!addr || typeof addr !== 'string' || addr.length < 20) return addr || '';
  const firstWord = addr.split(/[,\s]+/)[0].toLowerCase();
  if (firstWord.length < 3) return addr;
  let lastCommaIdx = addr.lastIndexOf(',');
  while (lastCommaIdx > 0) {
    const afterComma = addr.substring(lastCommaIdx + 1).trim();
    if (afterComma.toLowerCase().startsWith(firstWord)) {
      return addr.substring(0, lastCommaIdx).trim();
    }
    lastCommaIdx = addr.lastIndexOf(',', lastCommaIdx - 1);
  }
  return addr;
};

export function AddressComparison({ check, verificationData, setVerificationData, handleFieldVerification }) {
    const rawSD = check?.subcheck_data || {};
    const candidateData = {
      ...rawSD,
      complete_address: deduplicateAddress(rawSD.complete_address),
      father_name: check?.father_name || rawSD.father_name || '',
      dob: check?.dob || rawSD.dob || rawSD.date_of_birth || '',
      candidate_name: rawSD.candidate_name || rawSD.name || check?.employee_name || '',
    };
    
    // Verification status options with color codes
    const verificationStatusOptions = [
      { value: 'verified', label: 'Verified', color: 'bg-green-500', textColor: 'text-green-700', bgLight: 'bg-green-50 border-green-300' },
      { value: 'discrepancy', label: 'Discrepancy', color: 'bg-red-500', textColor: 'text-red-700', bgLight: 'bg-red-50 border-red-300' },
      { value: 'partially_verified', label: 'Partially Verified', color: 'bg-sky-400', textColor: 'text-sky-700', bgLight: 'bg-sky-50 border-sky-300' },
      { value: 'not_verified', label: 'Not Verified', color: 'bg-amber-500', textColor: 'text-amber-700', bgLight: 'bg-amber-50 border-amber-300' },
    ];
    
    // Get status color classes
    const getStatusClasses = (status) => {
      const found = verificationStatusOptions.find(o => o.value === status);
      return found || { color: 'bg-gray-300', textColor: 'text-gray-600', bgLight: 'bg-white border-gray-200' };
    };

    const addressTypes = [
      { value: 'present', label: 'Present Address' },
      { value: 'permanent', label: 'Permanent Address' },
      { value: 'native', label: 'Native Address' },
      { value: 'previous', label: 'Previous Address' },
    ];

    const residenceTypes = [
      { value: 'owned', label: 'Self-Owned' },
      { value: 'rented', label: 'Rented' },
      { value: 'family_owned', label: 'Family Owned' },
      { value: 'company_provided', label: 'Company Provided' },
      { value: 'paying_guest', label: 'Paying Guest (PG)' },
      { value: 'hostel', label: 'Hostel' },
    ];

    const relationshipOptions = [
      { value: 'self', label: 'Self' },
      { value: 'father', label: 'Father' },
      { value: 'mother', label: 'Mother' },
      { value: 'spouse', label: 'Spouse' },
      { value: 'sibling', label: 'Sibling' },
      { value: 'relative', label: 'Relative' },
      { value: 'neighbor', label: 'Neighbor' },
      { value: 'landlord', label: 'Landlord' },
      { value: 'security', label: 'Security Guard' },
      { value: 'other', label: 'Other' },
    ];
    
    // Clear form function for Address
    const clearAddressForm = () => {
      setVerificationData(prev => ({
        ...prev,
        verified_details: {
          ...prev.verified_details,
          candidate_name: '',
          name_status: '',
          father_name: '',
          father_status: '',
          dob: '',
          dob_status: '',
          period_of_stay: '',
          stay_status: '',
          complete_address: '',
          address_status: '',
          city: '',
          state: '',
          pin_code: '',
          person_met: '',
          relationship: '',
          person_contact: '',
          residence_type: '',
          house_type: '',
          locality_type: '',
          verification_method: '',
          address_confirmed_by: '',
          verification_date: '',
          gps_coordinates: '',
          field_remarks: '',
        }
      }));
      toast.info('Form cleared');
    };
    
    return (
      <div className="space-y-4">
        {/* Header with Clear Button */}
        <div className="flex items-center justify-between bg-gradient-to-r from-slate-800 to-slate-700 text-white px-4 py-3 rounded-lg shadow-lg">
          <h4 className="font-semibold text-base flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            Address Verification - {check?.subcheck_type || candidateData.address_type || 'Address'}
          </h4>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={clearAddressForm}
            className="bg-white/10 hover:bg-white/20 text-white border-white/30 hover:border-white/50"
          >
            <X className="w-4 h-4 mr-1" />
            Clear Form
          </Button>
        </div>
        
        {/* Status Color Legend */}
        <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg border">
          <span className="text-xs font-medium text-slate-600">Status Legend:</span>
          <div className="flex items-center gap-3">
            {verificationStatusOptions.map(opt => (
              <div key={opt.value} className="flex items-center gap-1">
                <div className={`w-3 h-3 rounded-full ${opt.color}`}></div>
                <span className={`text-xs ${opt.textColor}`}>{opt.label}</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Main Comparison Grid */}
        <div className="grid grid-cols-2 gap-4">
          {/* Candidate Provided (Left) */}
          <Card className="border-2 border-blue-200 shadow-md bg-gradient-to-b from-blue-50 to-white">
            <CardHeader className="pb-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-t-lg">
              <CardTitle className="text-sm flex items-center gap-2">
                <User className="w-4 h-4" />
                Candidate Provided
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm pt-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-2 bg-white rounded-lg border border-blue-100 shadow-sm">
                  <span className="text-blue-600 block text-xs font-medium">Candidate Name</span>
                  <span className="font-semibold text-slate-800">{candidateData.candidate_name || candidateData.name || check?.employee_name || '-'}</span>
                </div>
                <div className="p-2 bg-white rounded-lg border border-blue-100 shadow-sm">
                  <span className="text-blue-600 block text-xs font-medium">Verification Type</span>
                  <span className="font-semibold text-slate-800">{check?.subcheck_type || candidateData.address_type || 'Present Address'}</span>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="p-2 bg-white rounded-lg border border-blue-100 shadow-sm">
                  <span className="text-blue-600 block text-xs font-medium">Father's Name</span>
                  <span className="font-semibold text-slate-800">{candidateData.father_name || candidateData.fathers_name || '-'}</span>
                </div>
                <div className="p-2 bg-white rounded-lg border border-blue-100 shadow-sm">
                  <span className="text-blue-600 block text-xs font-medium">Date of Birth</span>
                  <span className="font-semibold text-slate-800">{candidateData.dob || candidateData.date_of_birth || '-'}</span>
                </div>
              </div>
              
              <div className="p-2 bg-white rounded-lg border border-blue-100 shadow-sm">
                <span className="text-blue-600 block text-xs font-medium">Period of Stay</span>
                <span className="font-semibold text-slate-800">{(() => {
                  // Try direct period_of_stay first
                  if (candidateData.period_of_stay || candidateData.duration) return candidateData.period_of_stay || candidateData.duration;
                  // Try calculating from FE form dates
                  const fv = check?.field_verification || {};
                  const av = check?.address_verification || {};
                  const stayFrom = fv.period_of_stay_from || av.field_period_of_stay_from || candidateData.stay_from || '';
                  const stayTo = fv.period_of_stay_to || av.field_period_of_stay_to || candidateData.stay_to || '';
                  if (stayFrom) {
                    const fromDate = new Date(stayFrom);
                    const toDate = stayTo ? new Date(stayTo) : new Date();
                    if (!isNaN(fromDate.getTime()) && !isNaN(toDate.getTime())) {
                      let years = toDate.getFullYear() - fromDate.getFullYear();
                      let months = toDate.getMonth() - fromDate.getMonth();
                      if (months < 0) { years--; months += 12; }
                      const parts = [];
                      if (years > 0) parts.push(`${years} year${years > 1 ? 's' : ''}`);
                      if (months > 0) parts.push(`${months} month${months > 1 ? 's' : ''}`);
                      return parts.join(' ') || 'Less than a month';
                    }
                    return `${stayFrom} - ${stayTo || 'Present'}`;
                  }
                  return '-';
                })()}</span>
              </div>
              
              <div className="p-2 bg-white rounded-lg border border-blue-100 shadow-sm">
                <span className="text-blue-600 block text-xs font-medium">Complete Address</span>
                <span className="font-semibold text-slate-800">{candidateData.complete_address || candidateData.address || candidateData.address_line1 || '-'}</span>
              </div>
              
              <div className="grid grid-cols-3 gap-3">
                <div className="p-2 bg-white rounded-lg border border-blue-100 shadow-sm">
                  <span className="text-blue-600 block text-xs font-medium">City</span>
                  <span className="font-semibold text-slate-800">{candidateData.city || '-'}</span>
                </div>
                <div className="p-2 bg-white rounded-lg border border-blue-100 shadow-sm">
                  <span className="text-blue-600 block text-xs font-medium">State</span>
                  <span className="font-semibold text-slate-800">{candidateData.state || '-'}</span>
                </div>
                <div className="p-2 bg-white rounded-lg border border-blue-100 shadow-sm">
                  <span className="text-blue-600 block text-xs font-medium">Pin Code</span>
                  <span className="font-semibold text-slate-800">{candidateData.pincode || candidateData.pin_code || '-'}</span>
                </div>
              </div>
              
              {candidateData.landmark && (
                <div className="p-2 bg-white rounded-lg border border-blue-100 shadow-sm">
                  <span className="text-blue-600 block text-xs font-medium">Landmark</span>
                  <span className="font-semibold text-slate-800">{candidateData.landmark}</span>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Verified Details (Right) */}
          <Card className="border-2 border-green-200 shadow-md bg-gradient-to-b from-green-50 to-white">
            <CardHeader className="pb-2 bg-gradient-to-r from-green-600 to-green-500 text-white rounded-t-lg">
              <CardTitle className="text-sm flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Verified Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-4">
              {/* Name with Color */}
              <div className={`p-3 rounded-lg border-2 shadow-sm transition-all ${getStatusClasses(verificationData.verified_details.name_status).bgLight}`}>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-semibold text-slate-700">Candidate Name</Label>
                  <Select 
                    value={verificationData.verified_details.name_status || ''} 
                    onValueChange={(v) => handleFieldVerification('name_status', true, v)}
                  >
                    <SelectTrigger className="h-7 w-36 text-xs border-slate-300">
                      <SelectValue placeholder="Select Status" />
                    </SelectTrigger>
                    <SelectContent>
                      {verificationStatusOptions.map(o => (
                        <SelectItem key={o.value} value={o.value}>
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${o.color}`}></div>
                            {o.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Input 
                  value={verificationData.verified_details.candidate_name || ''} 
                  onChange={(e) => handleFieldVerification('candidate_name', true, e.target.value)}
                  className="h-8 text-sm border-slate-200"
                  placeholder="Verified name"
                />
              </div>
              
              {/* Verification Type & Father's Name */}
              <div className="grid grid-cols-2 gap-2">
                <div className="p-2 bg-white rounded-lg border-2 border-green-100 shadow-sm">
                  <Label className="text-xs font-semibold text-slate-700 mb-1 block">Address Type</Label>
                  <Select 
                    value={verificationData.verified_details.address_type || ''} 
                    onValueChange={(v) => handleFieldVerification('address_type', true, v)}
                  >
                    <SelectTrigger className="h-7 text-sm border-slate-200"><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>
                      {addressTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className={`p-2 rounded-lg border-2 shadow-sm transition-all ${getStatusClasses(verificationData.verified_details.father_status).bgLight}`}>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-xs font-semibold text-slate-700">Father's Name</Label>
                    <Select 
                      value={verificationData.verified_details.father_status || ''} 
                      onValueChange={(v) => handleFieldVerification('father_status', true, v)}
                    >
                      <SelectTrigger className="h-5 w-20 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
                      <SelectContent>
                        {verificationStatusOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Input 
                    value={verificationData.verified_details.father_name || ''} 
                    onChange={(e) => handleFieldVerification('father_name', true, e.target.value)}
                    className="h-7 text-sm"
                    placeholder="Father's name"
                  />
                </div>
              </div>
              
              {/* DOB & Period of Stay */}
              <div className="grid grid-cols-2 gap-2">
                <div className="p-2 bg-white rounded-lg border">
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-xs font-medium">Date of Birth</Label>
                    <Select 
                      value={verificationData.verified_details.dob_status || ''} 
                      onValueChange={(v) => handleFieldVerification('dob_status', true, v)}
                    >
                      <SelectTrigger className="h-5 w-20 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
                      <SelectContent>
                        {verificationStatusOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Input 
                    type="date"
                    value={verificationData.verified_details.dob || ''} 
                    onChange={(e) => handleFieldVerification('dob', true, e.target.value)}
                    className="h-7 text-sm"
                  />
                </div>
                <div className="p-2 bg-white rounded-lg border">
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-xs font-medium">Period of Stay</Label>
                    <Select 
                      value={verificationData.verified_details.stay_status || ''} 
                      onValueChange={(v) => handleFieldVerification('stay_status', true, v)}
                    >
                      <SelectTrigger className="h-5 w-20 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
                      <SelectContent>
                        {verificationStatusOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Input 
                    value={verificationData.verified_details.period_of_stay || ''} 
                    onChange={(e) => handleFieldVerification('period_of_stay', true, e.target.value)}
                    className="h-7 text-sm"
                    placeholder="e.g., 3 years"
                  />
                </div>
              </div>
              
              {/* Address */}
              <div className="p-2 bg-white rounded-lg border">
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs font-medium">Complete Address</Label>
                  <Select 
                    value={verificationData.verified_details.address_status || ''} 
                    onValueChange={(v) => handleFieldVerification('address_status', true, v)}
                  >
                    <SelectTrigger className="h-6 w-28 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent>
                      {verificationStatusOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Textarea 
                  value={verificationData.verified_details.complete_address || ''} 
                  onChange={(e) => handleFieldVerification('complete_address', true, e.target.value)}
                  className="h-14 text-sm resize-none"
                  placeholder="Full verified address"
                />
              </div>
              
              {/* City, State, Pincode */}
              <div className="grid grid-cols-3 gap-2">
                <div className="p-2 bg-white rounded-lg border">
                  <Label className="text-xs font-medium mb-1 block">City</Label>
                  <Input 
                    value={verificationData.verified_details.city || ''} 
                    onChange={(e) => handleFieldVerification('city', true, e.target.value)}
                    className="h-7 text-sm"
                  />
                </div>
                <div className="p-2 bg-white rounded-lg border">
                  <Label className="text-xs font-medium mb-1 block">State</Label>
                  <Input 
                    value={verificationData.verified_details.state || ''} 
                    onChange={(e) => handleFieldVerification('state', true, e.target.value)}
                    className="h-7 text-sm"
                  />
                </div>
                <div className="p-2 bg-white rounded-lg border">
                  <Label className="text-xs font-medium mb-1 block">Pin Code</Label>
                  <Input 
                    value={verificationData.verified_details.pincode || ''} 
                    onChange={(e) => handleFieldVerification('pincode', true, e.target.value)}
                    className="h-7 text-sm"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Field Verification Section */}
        <Card className="border-amber-200">
          <CardHeader className="pb-2 bg-gradient-to-r from-amber-50 to-orange-50">
            <CardTitle className="text-sm text-amber-800 flex items-center gap-2">
              <User className="w-4 h-4" />
              Field Verification Details
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4 pt-3">
            <div className="space-y-3">
              <div className="p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">Person Met</Label>
                <Input 
                  value={verificationData.verified_details.person_met || ''} 
                  onChange={(e) => handleFieldVerification('person_met', true, e.target.value)}
                  className="h-7 text-sm"
                  placeholder="Name of person met during verification"
                />
              </div>
              <div className="p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">Relationship with Candidate</Label>
                <Select 
                  value={verificationData.verified_details.relationship || ''} 
                  onValueChange={(v) => handleFieldVerification('relationship', true, v)}
                >
                  <SelectTrigger className="h-7 text-sm"><SelectValue placeholder="Select relationship" /></SelectTrigger>
                  <SelectContent>
                    {relationshipOptions.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">Contact Number (Person Met)</Label>
                <Input 
                  value={verificationData.verified_details.person_contact || ''} 
                  onChange={(e) => handleFieldVerification('person_contact', true, e.target.value)}
                  className="h-7 text-sm"
                  placeholder="Phone number"
                />
              </div>
            </div>
            <div className="space-y-3">
              <div className="p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">Type of Residence</Label>
                <Select 
                  value={verificationData.verified_details.residence_type || ''} 
                  onValueChange={(v) => handleFieldVerification('residence_type', true, v)}
                >
                  <SelectTrigger className="h-7 text-sm"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {residenceTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">House/Building Type</Label>
                <Select 
                  value={verificationData.verified_details.building_type || ''} 
                  onValueChange={(v) => handleFieldVerification('building_type', true, v)}
                >
                  <SelectTrigger className="h-7 text-sm"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="independent_house">Independent House</SelectItem>
                    <SelectItem value="apartment">Apartment/Flat</SelectItem>
                    <SelectItem value="villa">Villa/Bungalow</SelectItem>
                    <SelectItem value="row_house">Row House</SelectItem>
                    <SelectItem value="chawl">Chawl/Tenement</SelectItem>
                    <SelectItem value="slum">Slum Area</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">Locality Type</Label>
                <Select 
                  value={verificationData.verified_details.locality_type || ''} 
                  onValueChange={(v) => handleFieldVerification('locality_type', true, v)}
                >
                  <SelectTrigger className="h-7 text-sm"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="posh">Posh/Upscale</SelectItem>
                    <SelectItem value="middle_class">Middle Class</SelectItem>
                    <SelectItem value="lower_middle">Lower Middle Class</SelectItem>
                    <SelectItem value="industrial">Industrial Area</SelectItem>
                    <SelectItem value="rural">Rural/Village</SelectItem>
                    <SelectItem value="slum">Slum Area</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Verification Confirmation Section */}
        <Card className="border-teal-200">
          <CardHeader className="pb-2 bg-gradient-to-r from-teal-50 to-cyan-50">
            <CardTitle className="text-sm text-teal-800 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Verification Confirmation
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4 pt-3">
            <div className="space-y-3">
              <div className="p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">Verification Method</Label>
                <Select 
                  value={verificationData.verified_details.address_verification_method || ''} 
                  onValueChange={(v) => handleFieldVerification('address_verification_method', true, v)}
                >
                  <SelectTrigger className="h-7 text-sm"><SelectValue placeholder="Select method" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="physical_visit">Physical Visit</SelectItem>
                    <SelectItem value="phone">Phone Verification</SelectItem>
                    <SelectItem value="video_call">Video Call</SelectItem>
                    <SelectItem value="neighbor">Neighbor Verification</SelectItem>
                    <SelectItem value="document">Document Based</SelectItem>
                    <SelectItem value="digital">Digital/Online</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">Address Confirmed By</Label>
                <Input 
                  value={verificationData.verified_details.confirmed_by || ''} 
                  onChange={(e) => handleFieldVerification('confirmed_by', true, e.target.value)}
                  className="h-7 text-sm"
                  placeholder="Name of verifier"
                />
              </div>
            </div>
            <div className="space-y-3">
              <div className="p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">Verification Date</Label>
                <Input 
                  type="date"
                  value={verificationData.verified_details.address_verification_date || new Date().toISOString().split('T')[0]} 
                  onChange={(e) => handleFieldVerification('address_verification_date', true, e.target.value)}
                  className="h-7 text-sm"
                />
              </div>
              <div className="p-2 bg-white rounded-lg border">
                <Label className="text-xs font-medium mb-1 block">GPS Coordinates (if available)</Label>
                <Input 
                  value={verificationData.verified_details.gps_coordinates || ''} 
                  onChange={(e) => handleFieldVerification('gps_coordinates', true, e.target.value)}
                  className="h-7 text-sm"
                  placeholder="Lat, Long"
                />
              </div>
            </div>
            <div className="col-span-2 p-2 bg-white rounded-lg border">
              <Label className="text-xs font-medium mb-1 block">Field Verification Remarks</Label>
              <Textarea 
                value={verificationData.verified_details.field_remarks || ''} 
                onChange={(e) => handleFieldVerification('field_remarks', true, e.target.value)}
                className="h-16 text-sm resize-none"
                placeholder="Additional observations during field verification..."
              />
            </div>
          </CardContent>
        </Card>
      </div>
    );
}
